import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { ArrowLeft, Loader2, Settings2, Globe, Bell, BookOpen, LayoutList, Moon, SlidersHorizontal, Smile, Sparkles, Sun } from 'lucide-react';
import {
  getLearnerPersianFontStack,
  isLearnerPersianFontId,
  LEARNER_PERSIAN_FONT_OPTIONS,
  LEARNER_SETTINGS_MODAL_CLOSED_EVENT,
  LEARNER_TIMEZONE_CHANGED_EVENT,
  type LearnerPersianFontId
} from '../../constants/learnerPersianFonts';
import { applyLearnerPersianFont, persistLearnerPersianFont, readStoredLearnerPersianFont } from '../../utils/applyLearnerPersianFont';
import { useFontSize } from '../learning/FontSizeContext';
import { FormattedTextBlock } from '../learning/FormattedTextBlock';
import { SectionPillSeparator } from '../shared/SectionPillSeparator';
import { authService, LearningPreferencesDto } from '../../services/authService';
import { disableWebPushOnThisDevice, enableWebPushFromUserGesture, PUSH_PREFERENCE_CHANGED_EVENT } from '../../services/pushService';
import { isWebPushSupported } from '../../utils/webPush';
import { isRunningAsInstalledPwa } from '../../utils/pwaInstallEnvironment';
import { InAppAlertsField, LEARNER_SETTINGS_IN_APP_ALERTS_ID } from './InAppAlertsField';
import { ensureTimeZoneInList, getAllIanaTimeZones, getDeviceTimeZone } from '../../utils/ianaTimeZones';
import { useNotifications } from '../feedback/NotificationSystem';
import { ProfileComboList } from '../shared/ProfileComboList';
import {
  LEARNER_SETTINGS_MODAL_CHROME_CLASS,
  LEARNER_SETTINGS_SECTION_THEMES
} from './learnerSettingsSectionThemes';
import { LanguageSwitcher } from '../shared/LanguageSwitcher';
import { useTranslation } from 'react-i18next';
import { useAppLocale } from '../../providers/LocaleProvider';
import {
  LEARNER_SETTINGS_MODAL_TAB_ORDER,
  LearnerSettingsModalTabs,
  learnerSettingsTabIcon,
  learnerSettingsTabCarouselIndex,
  learnerSettingsTabCarouselOrder,
  learnerSettingsTabForTourTarget,
  type LearnerSettingsModalTabId
} from './LearnerSettingsModalTabs';
import { LearnerTwoFactorSection } from './LearnerTwoFactorSection';
import {
  TOUR_PAGE_END_EVENT,
  TOUR_TARGET_FOCUS_EVENT,
  type TourPageEndEventDetail,
  type TourTargetEventDetail,
} from '../../learnerTour/tourTargetEvents';
import { useLearnerHomeDesign } from '../../utils/learnerHomeDesign';
import { useTheme } from '../../hooks/useTheme';
import { scrollOverlayTargetIntoView } from '../../utils/productTourPlacement';
import { MmePickBack, MmePickList } from '../dashboard/MmePickList';

/** Short RTL sample (lesson preview). */
const SAMPLE_FA =
  'فارسی زبان شیرین و کهنی است که ریشه‌های عمیقی در فرهنگ و ادب دارد. در این نمونه، جهت نوشتار از راست به چپ است تا حس خواندن یک درس واقعی را بگیرید.';

export interface LearnerSettingsPageProps {
  /** `modal` = embedded in {@link LearnerSettingsModal} (no full-page chrome). */
  variant?: 'page' | 'modal';
  onBack?: () => void;
  onClose?: () => void;
  onOpenProfileGoals?: () => void;
  initialTab?: LearnerSettingsModalTabId;
  /** Advanced modal: Help plays only this tab's tour. */
  onActiveTabChange?: (tab: LearnerSettingsModalTabId) => void;
  focusInAppAlerts?: boolean;
  /** Increments each time the Alerts row should be scrolled into view. */
  focusRequestId?: number;
}

export interface LearnerSettingsPageHandle {
  saveAll: () => Promise<void>;
  hasUnsavedChanges: () => boolean;
}

type PrefsBaseline = {
  timeZone: string;
  emailNotifications: boolean;
  pushNotifications: boolean;
  preferredPersianFontId: string;
  lessonTextFontMultiplier: number;
};

export const LearnerSettingsPage = forwardRef<LearnerSettingsPageHandle, LearnerSettingsPageProps>(function LearnerSettingsPage({
  variant = 'page',
  onBack,
  onClose,
  onOpenProfileGoals,
  initialTab,
  onActiveTabChange,
  focusInAppAlerts = false,
  focusRequestId = 0,
}, ref) {
  const isModal = variant === 'modal';
  const dismiss = isModal ? onClose : onBack;

  const { showSuccess, showError } = useNotifications();
  const { t } = useTranslation('settings');
  const { t: tCommon } = useTranslation('common');
  const { direction } = useAppLocale();
  const isRtl = direction === 'rtl';
  /** `dir={direction}` on the pane mirrors flex-row at sm+ (icon start/right in FA, start/left in EN). */
  const modalSectionHeaderRowClass = 'flex flex-col gap-4 sm:flex-row sm:items-start sm:gap-5';
  const modalIconTitleRowClass = 'flex items-center gap-2';
  const tabCarouselOrder = useMemo(() => learnerSettingsTabCarouselOrder(isRtl), [isRtl]);
  const { currentMultiplier, setFontSize } = useFontSize();

  const [persianFont, setPersianFont] = useState<LearnerPersianFontId>(() => {
    const r = readStoredLearnerPersianFont();
    return r === 'system' ? 'vazir' : r;
  });

  const deviceTimeZone = useMemo(() => getDeviceTimeZone(), []);
  const ianaTimeZones = useMemo(() => getAllIanaTimeZones(), []);

  const [prefs, setPrefs] = useState<LearningPreferencesDto | null>(null);
  const [loadingPrefs, setLoadingPrefs] = useState(true);
  const [savingPrefs, setSavingPrefs] = useState(false);
  const [savingRegion, setSavingRegion] = useState(false);
  const [savingAll, setSavingAll] = useState(false);
  const [savingLessonText, setSavingLessonText] = useState(false);
  const [resettingDefaults, setResettingDefaults] = useState(false);
  const [baseline, setBaseline] = useState<PrefsBaseline | null>(null);

  // Resolves a timezone string to a non-empty IANA value, falling back to the device zone
  // (or finally UTC). This guards against the server returning null/empty after a save on
  // environments where the column previously held no value.
  const resolveTimeZone = useCallback(
    (value: string | null | undefined): string => {
      const trimmed = typeof value === 'string' ? value.trim() : '';
      if (trimmed) return trimmed;
      return deviceTimeZone || 'UTC';
    },
    [deviceTimeZone]
  );

  // Build dropdown options. If the currently selected (or saved baseline) timezone isn't in
  // the canonical IANA list returned by the browser (e.g. legacy aliases or rare zones), we
  // still want to show it so the user never sees an empty selection.
  const tzOptions = useMemo(() => {
    const withCurrent = ensureTimeZoneInList(prefs?.timeZone, ianaTimeZones);
    const withBaseline = ensureTimeZoneInList(baseline?.timeZone, withCurrent);
    const withDevice = ensureTimeZoneInList(deviceTimeZone, withBaseline);
    return withDevice.map((z) => ({ value: z, label: z }));
  }, [ianaTimeZones, prefs?.timeZone, baseline?.timeZone, deviceTimeZone]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingPrefs(true);
      try {
        const p = await authService.getLearningPreferences();
        if (!cancelled) {
          const s = p.preferredPersianFontId?.trim().toLowerCase();
          if (s && isLearnerPersianFontId(s)) {
            setPersianFont(s === 'system' ? 'vazir' : s);
          } else {
            const r = readStoredLearnerPersianFont();
            setPersianFont(r === 'system' ? 'vazir' : r);
          }
          const resolvedTz = resolveTimeZone(p.timeZone);
          setPrefs({ ...p, timeZone: resolvedTz });
          const m = p.lessonTextFontMultiplier;
          if (typeof m === 'number' && Number.isFinite(m)) {
            setFontSize(m);
          }
          const fontId = (() => {
            const s = p.preferredPersianFontId?.trim().toLowerCase();
            if (s && isLearnerPersianFontId(s)) return s === 'system' ? 'vazir' : s;
            const r = readStoredLearnerPersianFont();
            return r === 'system' ? 'vazir' : r;
          })();
          setBaseline({
            timeZone: resolvedTz,
            emailNotifications: p.emailNotifications,
            pushNotifications: p.pushNotifications,
            preferredPersianFontId: fontId,
            lessonTextFontMultiplier: typeof m === 'number' && Number.isFinite(m) ? m : 1
          });
        }
      } catch {
        if (!cancelled) showError(t('loadFailed'));
      } finally {
        if (!cancelled) setLoadingPrefs(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [showError, setFontSize, resolveTimeZone, t]);

  useEffect(() => {
    const onPushPreference = (event: Event) => {
      const enabled = Boolean((event as CustomEvent<{ enabled?: boolean }>).detail?.enabled);
      setPrefs((prev) => (prev ? { ...prev, pushNotifications: enabled } : prev));
      setBaseline((prev) => (prev ? { ...prev, pushNotifications: enabled } : prev));
    };
    window.addEventListener(PUSH_PREFERENCE_CHANGED_EVENT, onPushPreference as EventListener);
    return () => window.removeEventListener(PUSH_PREFERENCE_CHANGED_EVENT, onPushPreference as EventListener);
  }, []);

  const updatePref = useCallback(<K extends keyof LearningPreferencesDto>(key: K, value: LearningPreferencesDto[K]) => {
    setPrefs((prev) => (prev ? { ...prev, [key]: value } : prev));
  }, []);

  const handlePushNotificationsChange = useCallback((checked: boolean) => {
    if (!checked) {
      setPushInstallOfferOpen(false);
      updatePref('pushNotifications', false);
      return;
    }
    if (!isRunningAsInstalledPwa()) {
      setPushInstallOfferOpen(true);
      return;
    }
    setPushInstallOfferOpen(false);
    updatePref('pushNotifications', true);
  }, [updatePref]);

  const syncBaseline = useCallback(
    (saved: LearningPreferencesDto) => {
      const fontId = (() => {
        const s = saved.preferredPersianFontId?.trim().toLowerCase();
        if (s && isLearnerPersianFontId(s)) return s === 'system' ? 'vazir' : s;
        return 'vazir';
      })();
      setBaseline({
        timeZone: resolveTimeZone(saved.timeZone),
        emailNotifications: saved.emailNotifications,
        pushNotifications: saved.pushNotifications,
        preferredPersianFontId: fontId,
        lessonTextFontMultiplier: saved.lessonTextFontMultiplier ?? 1
      });
    },
    [resolveTimeZone]
  );

  type SettingsSection = 'region' | 'notifications' | 'lessonText' | 'all';

  const buildSectionPayload = useCallback(
    (section: SettingsSection): LearningPreferencesDto | null => {
      if (!prefs || !baseline) return null;
      const base = { ...prefs };
      const currentTz = resolveTimeZone(prefs.timeZone);
      const baselineTz = resolveTimeZone(baseline.timeZone);
      switch (section) {
        case 'region':
          return {
            ...base,
            timeZone: currentTz,
            emailNotifications: baseline.emailNotifications,
            pushNotifications: baseline.pushNotifications,
            preferredPersianFontId: baseline.preferredPersianFontId,
            lessonTextFontMultiplier: baseline.lessonTextFontMultiplier
          };
        case 'notifications':
          return {
            ...base,
            timeZone: baselineTz,
            emailNotifications: prefs.emailNotifications,
            pushNotifications: prefs.pushNotifications,
            preferredPersianFontId: baseline.preferredPersianFontId,
            lessonTextFontMultiplier: baseline.lessonTextFontMultiplier
          };
        case 'lessonText':
          return {
            ...base,
            timeZone: baselineTz,
            emailNotifications: baseline.emailNotifications,
            pushNotifications: baseline.pushNotifications,
            preferredPersianFontId: persianFont,
            lessonTextFontMultiplier: currentMultiplier
          };
        case 'all':
          return {
            ...base,
            timeZone: currentTz,
            emailNotifications: prefs.emailNotifications,
            pushNotifications: prefs.pushNotifications,
            preferredPersianFontId: persianFont,
            lessonTextFontMultiplier: currentMultiplier
          };
      }
    },
    [prefs, baseline, persianFont, currentMultiplier, resolveTimeZone]
  );

  const mergeAfterSectionSave = useCallback(
    (saved: LearningPreferencesDto, section: SettingsSection) => {
      const savedTz = resolveTimeZone(saved.timeZone);
      setPrefs((prev) => {
        if (!prev) return { ...saved, timeZone: savedTz };
        const next = { ...saved, timeZone: savedTz };
        if (section !== 'region' && section !== 'all' && (prev.timeZone || 'UTC') !== (baseline?.timeZone ?? '')) {
          next.timeZone = resolveTimeZone(prev.timeZone);
        }
        if (
          section !== 'notifications' &&
          section !== 'all' &&
          baseline &&
          (prev.emailNotifications !== baseline.emailNotifications || prev.pushNotifications !== baseline.pushNotifications)
        ) {
          next.emailNotifications = prev.emailNotifications;
          next.pushNotifications = prev.pushNotifications;
        }
        if (section !== 'lessonText' && section !== 'all') {
          const savedFont = saved.preferredPersianFontId?.trim().toLowerCase();
          const prevFont = prev.preferredPersianFontId?.trim().toLowerCase();
          const savedMult = saved.lessonTextFontMultiplier ?? 1;
          if (persianFont !== (savedFont && isLearnerPersianFontId(savedFont) ? savedFont : 'vazir') || Math.abs(currentMultiplier - savedMult) > 0.001) {
            next.preferredPersianFontId = persianFont;
            next.lessonTextFontMultiplier = currentMultiplier;
          }
        }
        return next;
      });
      setBaseline((b) => {
        if (!b) return b;
        const next = { ...b };
        if (section === 'region' || section === 'all') {
          next.timeZone = savedTz;
        }
        if (section === 'notifications' || section === 'all') {
          next.emailNotifications = saved.emailNotifications;
          next.pushNotifications = saved.pushNotifications;
        }
        if (section === 'lessonText' || section === 'all') {
          const s = saved.preferredPersianFontId?.trim().toLowerCase();
          next.preferredPersianFontId = s && isLearnerPersianFontId(s) ? (s === 'system' ? 'vazir' : s) : 'vazir';
          next.lessonTextFontMultiplier = saved.lessonTextFontMultiplier ?? 1;
        }
        return next;
      });
    },
    [baseline, persianFont, currentMultiplier, resolveTimeZone]
  );

  const savedFontOnAccount = useMemo(() => {
    const s = prefs?.preferredPersianFontId?.trim().toLowerCase();
    if (!s || !isLearnerPersianFontId(s)) return '';
    return s === 'system' ? 'vazir' : s;
  }, [prefs]);

  const fontDirty = useMemo(() => {
    if (!prefs) return false;
    const stored = readStoredLearnerPersianFont();
    const storedNorm = stored === 'system' ? 'vazir' : stored;
    const baseline = savedFontOnAccount || storedNorm;
    return persianFont !== baseline;
  }, [prefs, persianFont, savedFontOnAccount]);

  const sizeDirty = useMemo(() => {
    if (!prefs) return false;
    const savedM = prefs.lessonTextFontMultiplier ?? 1;
    return Math.abs(currentMultiplier - savedM) > 0.001;
  }, [prefs, currentMultiplier]);

  const lessonTextDirty = fontDirty || sizeDirty;

  const regionDirty = useMemo(() => {
    if (!prefs || !baseline) return false;
    return resolveTimeZone(prefs.timeZone) !== resolveTimeZone(baseline.timeZone);
  }, [prefs, baseline, resolveTimeZone]);

  const notifDirty = useMemo(() => {
    if (!prefs || !baseline) return false;
    return (
      prefs.emailNotifications !== baseline.emailNotifications ||
      prefs.pushNotifications !== baseline.pushNotifications
    );
  }, [prefs, baseline]);

  const hasUnsavedChanges = regionDirty || notifDirty || lessonTextDirty;

  const [activeModalTab, setActiveModalTab] = useState<LearnerSettingsModalTabId>(initialTab ?? 'security');

  useEffect(() => {
    if (!isModal || !onActiveTabChange) return;
    onActiveTabChange(activeModalTab);
  }, [isModal, activeModalTab, onActiveTabChange]);
  const [homeDesign, setHomeDesign] = useLearnerHomeDesign();
  const { theme, toggleTheme } = useTheme();
  const [colorModePending, setColorModePending] = useState(false);
  const simpleHome = homeDesign === 'mme';
  const [settingsMenu, setSettingsMenu] = useState(() => isModal && !initialTab);
  const [pushInstallOfferOpen, setPushInstallOfferOpen] = useState(false);
  const [installOfferOccupying, setInstallOfferOccupying] = useState(false);
  const handleInstallOfferOccupyingChange = useCallback((occupying: boolean) => {
    setInstallOfferOccupying(occupying);
  }, []);
  const [inAppAlertsFocused, setInAppAlertsFocused] = useState(false);
  const [dragOffsetPx, setDragOffsetPx] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [tabMotionReady, setTabMotionReady] = useState(false);
  const [tourSnap, setTourSnap] = useState(false);
  const activeModalTabRef = useRef(activeModalTab);
  const dragOffsetPxRef = useRef(0);
  const dragUiRef = useRef({ isDragging: false, dragOffsetPx: 0 });
  const isRtlRef = useRef(isRtl);
  const cardsScrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    isRtlRef.current = isRtl;
  }, [isRtl]);

  useLayoutEffect(() => {
    activeModalTabRef.current = activeModalTab;
  }, [activeModalTab]);

  useLayoutEffect(() => {
    if (!isModal) return undefined;
    const id = requestAnimationFrame(() => setTabMotionReady(true));
    return () => cancelAnimationFrame(id);
  }, [isModal]);

  const handleModalTabChange = useCallback((next: LearnerSettingsModalTabId) => {
    if (next === activeModalTabRef.current) return;
    setActiveModalTab(next);
  }, []);

  useEffect(() => {
    if (!initialTab) return;
    setSettingsMenu(false);
    setActiveModalTab(initialTab);
  }, [initialTab, focusRequestId]);

  useEffect(() => {
    if (!focusInAppAlerts || !focusRequestId) return undefined;
    setSettingsMenu(false);
    setActiveModalTab('notifications');
    setInAppAlertsFocused(true);
    const scrollTimer = window.setTimeout(() => {
      const el = document.getElementById(LEARNER_SETTINGS_IN_APP_ALERTS_ID);
      el?.scrollIntoView({
        block: 'center',
        behavior: 'smooth',
      });
      el?.focus({ preventScroll: true });
    }, 320);
    const clearTimer = window.setTimeout(() => setInAppAlertsFocused(false), 2600);
    return () => {
      window.clearTimeout(scrollTimer);
      window.clearTimeout(clearTimer);
    };
  }, [focusInAppAlerts, focusRequestId]);

  useEffect(() => {
    if (prefs?.pushNotifications && !isRunningAsInstalledPwa()) {
      setPushInstallOfferOpen(true);
    }
  }, [prefs?.pushNotifications]);

  useEffect(() => {
    if (!isModal) return undefined;
    const onFocus = (event: Event) => {
      const detail = (event as CustomEvent<TourTargetEventDetail>).detail;
      const key = detail?.targetKey ?? '';
      const tab = key ? learnerSettingsTabForTourTarget(key) : undefined;
      if (!tab) return;
      flushSync(() => {
        setTourSnap(true);
        if (simpleHome) setSettingsMenu(true);
        setActiveModalTab(tab);
      });
      const escaped = typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(key) : key.replace(/"/g, '\\"');
      const nodes = document.querySelectorAll(`[data-tour-id="${escaped}"]`);
      for (const node of nodes) {
        if (!(node instanceof HTMLElement) || node.closest('[hidden]') || node.closest('[role="tablist"]')) continue;
        scrollOverlayTargetIntoView(node);
        break;
      }
    };
    const onEnd = (event: Event) => {
      const pageKey = (event as CustomEvent<TourPageEndEventDetail>).detail?.pageKey ?? '';
      if (!pageKey.startsWith('quick-settings')) return;
      setTourSnap(false);
      setTabMotionReady(false);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setTabMotionReady(true));
      });
    };
    window.addEventListener(TOUR_TARGET_FOCUS_EVENT, onFocus);
    window.addEventListener(TOUR_PAGE_END_EVENT, onEnd);
    return () => {
      window.removeEventListener(TOUR_TARGET_FOCUS_EVENT, onFocus);
      window.removeEventListener(TOUR_PAGE_END_EVENT, onEnd);
    };
  }, [isModal, simpleHome]);

  const modalDirtyTabs = useMemo(
    () => ({
      region: regionDirty,
      notifications: notifDirty,
      lessonText: lessonTextDirty
    }),
    [regionDirty, notifDirty, lessonTextDirty]
  );

  /** Touch carousel between settings tabs — same RTL-aware behavior as profile / comments tabs. */
  useEffect(() => {
    if (!isModal || simpleHome) return;
    const el = cardsScrollRef.current;
    if (!el) return;

    let startX = 0;
    let startY = 0;
    let lastX = 0;
    let dragging = false;
    let axis: 'horizontal' | 'vertical' | null = null;
    const SWIPE_DISTANCE_PX = 50;
    const SWIPE_VELOCITY_PX_PER_MS = 0.4;
    let startTime = 0;

    const onTouchStart = (e: TouchEvent) => {
      const t = e.touches[0];
      if (!t || e.touches.length !== 1) return;
      startX = t.clientX;
      startY = t.clientY;
      lastX = t.clientX;
      startTime = Date.now();
      dragging = false;
      axis = null;
    };

    const onTouchMove = (e: TouchEvent) => {
      const t = e.touches[0];
      if (!t || e.touches.length !== 1) return;
      const dx = t.clientX - startX;
      const dy = t.clientY - startY;
      lastX = t.clientX;

      if (axis === null) {
        if (Math.abs(dx) > 8 || Math.abs(dy) > 8) {
          axis = Math.abs(dx) > Math.abs(dy) ? 'horizontal' : 'vertical';
        }
      }
      if (axis === 'vertical') {
        if (dragging) {
          dragging = false;
          setIsDragging(false);
          dragOffsetPxRef.current = 0;
          setDragOffsetPx(0);
        }
        return;
      }
      if (axis !== 'horizontal') return;

      if (!dragging) {
        dragging = true;
        setIsDragging(true);
      }
      const idx = LEARNER_SETTINGS_MODAL_TAB_ORDER.indexOf(activeModalTabRef.current);
      const width = el.clientWidth || 1;
      const rtl = isRtlRef.current;
      let offset = dx;
      const atFirstRubber = rtl ? idx === 0 && offset < 0 : idx === 0 && offset > 0;
      const atLastRubber =
        rtl ? idx === LEARNER_SETTINGS_MODAL_TAB_ORDER.length - 1 && offset > 0 : idx === LEARNER_SETTINGS_MODAL_TAB_ORDER.length - 1 && offset < 0;
      if (atFirstRubber || atLastRubber) {
        offset = offset / 3;
      }
      offset = Math.max(-width, Math.min(width, offset));
      dragOffsetPxRef.current = offset;
      dragUiRef.current = { isDragging: true, dragOffsetPx: offset };
      setDragOffsetPx(offset);
      e.preventDefault();
    };

    const finishDrag = () => {
      const offset = dragOffsetPxRef.current;
      const width = el.clientWidth || 1;
      const elapsed = Math.max(1, Date.now() - startTime);
      const velocity = (lastX - startX) / elapsed;
      const idx = LEARNER_SETTINGS_MODAL_TAB_ORDER.indexOf(activeModalTabRef.current);
      const rtl = isRtlRef.current;
      let nextIdx = idx;
      if (rtl) {
        if ((offset > SWIPE_DISTANCE_PX || velocity > SWIPE_VELOCITY_PX_PER_MS) && idx < LEARNER_SETTINGS_MODAL_TAB_ORDER.length - 1) {
          nextIdx = idx + 1;
        } else if ((offset < -SWIPE_DISTANCE_PX || velocity < -SWIPE_VELOCITY_PX_PER_MS) && idx > 0) {
          nextIdx = idx - 1;
        } else if (Math.abs(offset) > width / 2) {
          nextIdx = offset > 0
            ? Math.min(LEARNER_SETTINGS_MODAL_TAB_ORDER.length - 1, idx + 1)
            : Math.max(0, idx - 1);
        }
      } else if ((offset < -SWIPE_DISTANCE_PX || velocity < -SWIPE_VELOCITY_PX_PER_MS) && idx < LEARNER_SETTINGS_MODAL_TAB_ORDER.length - 1) {
        nextIdx = idx + 1;
      } else if ((offset > SWIPE_DISTANCE_PX || velocity > SWIPE_VELOCITY_PX_PER_MS) && idx > 0) {
        nextIdx = idx - 1;
      } else if (Math.abs(offset) > width / 2) {
        nextIdx = offset < 0
          ? Math.min(LEARNER_SETTINGS_MODAL_TAB_ORDER.length - 1, idx + 1)
          : Math.max(0, idx - 1);
      }
      dragOffsetPxRef.current = 0;
      setDragOffsetPx(0);
      setIsDragging(false);
      const nextTab = LEARNER_SETTINGS_MODAL_TAB_ORDER[nextIdx];
      const currentTab = activeModalTabRef.current;
      if (nextTab && nextTab !== currentTab) {
        activeModalTabRef.current = nextTab;
        setActiveModalTab(nextTab);
      }
    };

    const onTouchEnd = () => {
      if (!dragging) return;
      dragging = false;
      finishDrag();
    };

    const onTouchCancel = () => {
      if (!dragging) return;
      dragging = false;
      dragOffsetPxRef.current = 0;
      setDragOffsetPx(0);
      setIsDragging(false);
    };

    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchmove', onTouchMove, { passive: false });
    el.addEventListener('touchend', onTouchEnd, { passive: true });
    el.addEventListener('touchcancel', onTouchCancel, { passive: true });
    return () => {
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      el.removeEventListener('touchend', onTouchEnd);
      el.removeEventListener('touchcancel', onTouchCancel);
    };
  }, [isModal, simpleHome]);

  const handlePersianFontSelect = (id: LearnerPersianFontId) => {
    setPersianFont(id);
    void (async () => {
      const row = LEARNER_PERSIAN_FONT_OPTIONS.find((o) => o.id === id);
      const primary = row?.cssFamily;
      if (!primary) return;
      try {
        await document.fonts?.load?.(`400 18px "${primary}"`);
      } catch {
        /* ignore */
      }
    })();
  };

  const handleResetToDefaultFontAndSize = async () => {
    const id: LearnerPersianFontId = 'vazir';
    setResettingDefaults(true);
    try {
      handlePersianFontSelect(id);
      setFontSize(1);
      persistLearnerPersianFont(id);
      applyLearnerPersianFont(id);

      if (prefs) {
        const payload = buildSectionPayload('lessonText') ?? prefs;
        const saved = await authService.updateLearningPreferences({
          ...payload,
          preferredPersianFontId: id,
          lessonTextFontMultiplier: 1
        });
        mergeAfterSectionSave(saved, 'lessonText');
      }
      showSuccess(t('resetSuccess'));
      window.dispatchEvent(new CustomEvent(LEARNER_SETTINGS_MODAL_CLOSED_EVENT));
    } catch (e) {
      showError(e instanceof Error ? e.message : t('resetFailed'));
    } finally {
      setResettingDefaults(false);
    }
  };

  const handleSaveLessonText = async () => {
    if (!prefs || savingLessonText || !lessonTextDirty) return;
    const payload = buildSectionPayload('lessonText');
    if (!payload) return;
    setSavingLessonText(true);
    try {
      const saved = await authService.updateLearningPreferences(payload);
      mergeAfterSectionSave(saved, 'lessonText');
      persistLearnerPersianFont(persianFont);
      applyLearnerPersianFont(persianFont);
      showSuccess(t('lessonTextSaved'));
    } catch (e) {
      showError(e instanceof Error ? e.message : t('saveFailed'));
    } finally {
      setSavingLessonText(false);
    }
  };

  const handleSaveRegion = async () => {
    if (!prefs || savingRegion || !regionDirty) return;
    const payload = buildSectionPayload('region');
    if (!payload) return;
    setSavingRegion(true);
    try {
      const saved = await authService.updateLearningPreferences(payload);
      mergeAfterSectionSave(saved, 'region');
      const savedTz = resolveTimeZone(saved.timeZone);
      // Notify listeners (e.g. Profile → Progress activity chart) so they can re-fetch
      // server-side timezone-bucketed data using the freshly saved zone.
      window.dispatchEvent(
        new CustomEvent(LEARNER_TIMEZONE_CHANGED_EVENT, { detail: { timeZone: savedTz } })
      );
      showSuccess(t('regionSaved'));
    } catch (e) {
      showError(e instanceof Error ? e.message : t('saveFailed'));
    } finally {
      setSavingRegion(false);
    }
  };

  const handleSaveGoalsPrefs = async () => {
    if (!prefs || savingPrefs || !notifDirty) return;
    const payload = buildSectionPayload('notifications');
    if (!payload) return;
    setSavingPrefs(true);
    try {
      let pushHint: 'denied' | 'unsupported' | null = null;
      if (payload.pushNotifications) {
        const pushResult = await enableWebPushFromUserGesture();
        if (pushResult === 'denied') {
          pushHint = 'denied';
          payload.pushNotifications = false;
        } else if (pushResult === 'unsupported' && !isWebPushSupported()) {
          pushHint = 'unsupported';
          payload.pushNotifications = false;
        }
      } else {
        await disableWebPushOnThisDevice();
      }
      const saved = await authService.updateLearningPreferences(payload);
      mergeAfterSectionSave(saved, 'notifications');
      window.dispatchEvent(
        new CustomEvent(PUSH_PREFERENCE_CHANGED_EVENT, { detail: { enabled: saved.pushNotifications } })
      );
      if (pushHint === 'denied') {
        showError(t('pushBlocked', { defaultValue: 'Notifications are blocked in the browser. Enable them in system settings to get message alerts on this device.' }));
      } else if (pushHint === 'unsupported') {
        setPushInstallOfferOpen(true);
      } else {
        showSuccess(t('notificationsSaved'));
      }
    } catch (e) {
      showError(e instanceof Error ? e.message : t('saveFailed'));
    } finally {
      setSavingPrefs(false);
    }
  };

  const handleSaveAll = useCallback(async () => {
    if (!prefs || savingAll || !hasUnsavedChanges) return;
    const payload = buildSectionPayload('all');
    if (!payload) return;
    const tzChanged = regionDirty;
    setSavingAll(true);
    try {
      if (notifDirty) {
        if (payload.pushNotifications) {
          const pushResult = await enableWebPushFromUserGesture();
          if (pushResult === 'unsupported' && !isWebPushSupported()) {
            payload.pushNotifications = false;
          }
        } else {
          await disableWebPushOnThisDevice();
        }
      }
      const saved = await authService.updateLearningPreferences(payload);
      const savedTz = resolveTimeZone(saved.timeZone);
      setPrefs({ ...saved, timeZone: savedTz });
      syncBaseline(saved);
      window.dispatchEvent(
        new CustomEvent(PUSH_PREFERENCE_CHANGED_EVENT, { detail: { enabled: saved.pushNotifications } })
      );
      persistLearnerPersianFont(persianFont);
      applyLearnerPersianFont(persianFont);
      if (tzChanged) {
        window.dispatchEvent(
          new CustomEvent(LEARNER_TIMEZONE_CHANGED_EVENT, { detail: { timeZone: savedTz } })
        );
      }
      showSuccess(t('allSaved'));
      if (isModal) dismiss?.();
    } catch (e) {
      showError(e instanceof Error ? e.message : t('saveFailed'));
    } finally {
      setSavingAll(false);
    }
  }, [prefs, savingAll, hasUnsavedChanges, regionDirty, notifDirty, buildSectionPayload, syncBaseline, persianFont, applyLearnerPersianFont, resolveTimeZone, showSuccess, showError, isModal, dismiss]);

  useImperativeHandle(
    ref,
    () => ({
      saveAll: handleSaveAll,
      hasUnsavedChanges: () => hasUnsavedChanges
    }),
    [hasUnsavedChanges, handleSaveAll]
  );

  const tabLabelKeys: Record<LearnerSettingsModalTabId, string> = {
    region: 'tabRegion',
    security: 'tabSecurity',
    notifications: 'notifications',
    lessonText: 'tabLessonText',
    template: 'tabPreferences',
  };

  const percent = Math.round(currentMultiplier * 100);

  const regionTheme = LEARNER_SETTINGS_SECTION_THEMES.sky;
  const notifTheme = LEARNER_SETTINGS_SECTION_THEMES.amber;
  const lessonTheme = LEARNER_SETTINGS_SECTION_THEMES.violet;
  const securityTheme = LEARNER_SETTINGS_SECTION_THEMES.emerald;
  const sectionPad = isModal ? 'mb-0 p-4 sm:p-5' : 'mb-0 p-5 sm:p-6';
  const sectionPadLast = isModal ? 'p-4 sm:p-5' : 'p-5 sm:p-6';
  const outerClass = isModal
    ? 'flex min-h-0 w-full flex-1 flex-col bg-transparent'
    : 'min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 dark:from-gray-900 dark:to-gray-950 pb-28';
  const innerClass = isModal
    ? 'relative min-h-0 flex-1 overflow-hidden'
    : 'max-w-2xl mx-auto px-4 py-6 sm:py-10';
  const modalPanelPaddingClass = 'px-4 pt-2 pb-4 sm:px-5';

  return (
    <div className={outerClass}>
      {isModal && simpleHome && settingsMenu ? (
        <MmePickList
          items={LEARNER_SETTINGS_MODAL_TAB_ORDER.map((tabId) => ({
            id: tabId,
            title: t(
              tabLabelKeys[tabId],
              tabId === 'template' ? { defaultValue: isRtl ? 'ترجیحات' : 'Preferences' } : {}
            ),
            icon: learnerSettingsTabIcon(tabId),
            tourId: `quick-settings-${tabId === 'lessonText' ? 'lesson-text' : tabId}`,
          }))}
          onPick={(id) => {
            setSettingsMenu(false);
            handleModalTabChange(id as LearnerSettingsModalTabId);
          }}
        />
      ) : null}
      {isModal && simpleHome && !settingsMenu ? (
        <div className="px-3 pt-2">
          <MmePickBack onBack={() => setSettingsMenu(true)} />
        </div>
      ) : null}
      {isModal && !simpleHome && (
        <LearnerSettingsModalTabs
          active={activeModalTab}
          onChange={handleModalTabChange}
          dirtyTabs={modalDirtyTabs}
          dragOffsetPx={dragOffsetPx}
          isDragging={isDragging}
          carouselRef={cardsScrollRef}
        />
      )}
      <div className={innerClass}>
        {!isModal && (
          <div className="flex items-center gap-3 mb-8">
            <button
              type="button"
              onClick={() => dismiss?.()}
              className="p-2 rounded-xl border border-slate-200 dark:border-gray-600 bg-white dark:bg-gray-800 shadow-sm hover:bg-slate-50 dark:hover:bg-gray-700 transition-colors"
              aria-label={t('back', { ns: 'common' })}
            >
              <ArrowLeft className="w-5 h-5 text-slate-700 dark:text-slate-200" />
            </button>
            <div className="flex items-center gap-2">
              <Settings2 className="w-7 h-7 text-teal-600 dark:text-teal-400" />
              <h1 className="text-2xl font-semibold text-slate-900 dark:text-white tracking-tight">{t('title')}</h1>
            </div>
          </div>
        )}

        {!isModal && (
          <p className="mme-hint text-sm text-slate-600 dark:text-slate-400 leading-relaxed mb-8">
            {t('pageIntro')}
            {onOpenProfileGoals && (
              <>
                {' '}
                <button
                  type="button"
                  className="text-teal-700 dark:text-teal-400 underline font-medium"
                  onClick={onOpenProfileGoals}
                >
                  {t('openGoalsAndTime')}
                </button>
              </>
            )}
          </p>
        )}

        {isModal ? (
          <div dir="ltr" className={`absolute inset-0 overflow-hidden ${simpleHome && settingsMenu ? 'hidden' : ''}`} hidden={simpleHome && settingsMenu ? true : undefined}>
            <div
              ref={cardsScrollRef}
              className="flex h-full w-full min-w-0 items-start"
              style={{
                touchAction: 'pan-y',
                transform: simpleHome || tourSnap
                  ? 'none'
                  : `translate3d(calc(-${learnerSettingsTabCarouselIndex(activeModalTab, isRtl) * 100}% + ${dragOffsetPx}px), 0, 0)`,
                transition: simpleHome || tourSnap || isDragging || !tabMotionReady ? 'none' : 'transform 0.25s ease-out'
              }}
            >
              {(simpleHome || tourSnap ? [activeModalTab] : tabCarouselOrder).map((tabId) => (
                <div
                  key={tabId}
                  role="tabpanel"
                  data-settings-panel={tabId === activeModalTab ? 'open' : 'closed'}
                  aria-label={t('settingsTabPanel', {
                    tab: t(
                      tabLabelKeys[tabId],
                      tabId === 'template' ? { defaultValue: isRtl ? 'ترجیحات' : 'Preferences' } : {}
                    ),
                  })}
                  className={`h-full min-w-0 shrink-0 self-stretch overflow-x-hidden overflow-y-auto overscroll-y-contain ${
                    simpleHome || tourSnap ? 'w-full' : 'flex-[0_0_100%]'
                  }`}
                >
                  <div className={modalPanelPaddingClass} dir={direction}>
              {tabId === 'region' && (
        <section className={`${regionTheme.section} ${sectionPad}`}>
          <div className={modalSectionHeaderRowClass}>
            <div className={regionTheme.icon}>
              <Globe className="h-6 w-6" aria-hidden />
            </div>
            <div className="min-w-0 flex-1 text-start">
              <h2 className="mb-1 text-lg font-semibold tracking-tight text-slate-900 dark:text-white">{t('regionTitle')}</h2>
              <p className="mme-hint mb-5 max-w-prose text-xs text-slate-500 dark:text-slate-400">
                {t('regionHint')}
              </p>

              {loadingPrefs || !prefs ? (
                <div className="flex items-center justify-center gap-2 rounded-xl border border-dashed border-slate-200 bg-white/50 py-6 text-slate-600 dark:border-gray-600 dark:bg-gray-900/40 dark:text-slate-300">
                  <Loader2 className={`h-6 w-6 animate-spin ${regionTheme.loader}`} />
                  <span>{t('loadingPreferences')}</span>
                </div>
              ) : (
                <>
                  <div
                    data-tour-id="quick-settings-region-language"
                    className="mb-6 min-w-0 max-w-full rounded-xl border border-slate-200/80 bg-white/60 p-4 text-start dark:border-gray-600 dark:bg-gray-900/40"
                  >
                    <p className="mb-1 text-sm font-medium text-slate-800 dark:text-slate-100">{t('preferredLanguage')}</p>
                    <p className="mme-hint mb-3 text-xs text-slate-500 dark:text-slate-400">{t('preferredLanguageHint')}</p>
                    <LanguageSwitcher variant="settings" />
                  </div>

                  <div className="min-w-0 max-w-full" data-tour-id="quick-settings-region-timezone">
                  <label htmlFor="settings-tz" className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-200">
                    {t('timezone')}
                  </label>
                  <div id="settings-tz">
                    <ProfileComboList
                      value={resolveTimeZone(prefs.timeZone)}
                      onChange={(tz) => updatePref('timeZone', resolveTimeZone(tz))}
                      options={tzOptions}
                      searchable
                      searchPlaceholder={t('searchTimeZones')}
                      placeholder={t('selectTimeZone')}
                      id="learner-settings-timezone"
                      aria-label={t('timezone')}
                      useFixedPortal
                      closeOnOutsideClick={false}
                      accentSelectedClass={regionTheme.accentSelected}
                      accentCheckClass={regionTheme.accentCheck}
                      triggerClassName="border-slate-200 dark:border-gray-600"
                    />
                  </div>
                  </div>
                  <div className="mt-4 flex justify-center">
                  <button
                    type="button"
                    onClick={() => void handleSaveRegion()}
                    disabled={!regionDirty || savingRegion || loadingPrefs}
                    className={`flex w-full items-center justify-center gap-2 sm:w-auto ${regionTheme.saveButton}`}
                  >
                    {savingRegion && <Loader2 className="h-4 w-4 animate-spin" />}
                    {t('saveRegion')}
                  </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </section>
              )}

              {tabId === 'security' && (
                <div>
                <LearnerTwoFactorSection
                  theme={securityTheme}
                  sectionPad={sectionPad}
                  isModal={isModal}
                />
                </div>
              )}

              {tabId === 'notifications' && (
        <section className={`${notifTheme.section} ${sectionPad}`}>
          <div className={modalSectionHeaderRowClass}>
            <div className={notifTheme.icon}>
              <Bell className="h-6 w-6" aria-hidden />
            </div>
            <div className="min-w-0 flex-1 text-start">
              <h2 className="mb-1 text-lg font-semibold tracking-tight text-slate-900 dark:text-white">{t('notifications')}</h2>
              <p className="mme-hint mb-4 max-w-prose text-xs text-slate-500 dark:text-slate-400">
                {t('notificationsHint')}
              </p>

              {loadingPrefs || !prefs ? (
                <div className="flex items-center justify-center gap-2 rounded-xl border border-dashed border-slate-200 bg-white/50 py-6 text-slate-600 dark:border-gray-600 dark:bg-gray-900/40 dark:text-slate-300">
                  <Loader2 className={`h-6 w-6 animate-spin ${notifTheme.loader}`} />
                  <span>{t('loadingPreferences')}</span>
                </div>
              ) : (
                <>
                  <p className="mb-1 text-xs font-medium text-slate-600 dark:text-slate-300">{t('howWeReachYou')}</p>
                  <p className="mme-hint mb-3 max-w-prose text-[11px] leading-relaxed text-slate-500 dark:text-slate-400">
                    {t('howWeReachYouHint')}
                  </p>
                  <div className="mb-6 grid grid-cols-1 gap-3">
                    <label
                      data-tour-id="quick-settings-notifications-email"
                      className={`flex cursor-pointer items-start justify-between gap-3 rounded-xl border border-slate-200/90 bg-white/95 px-4 py-3.5 shadow-sm transition-colors dark:border-gray-600 dark:bg-gray-900/60 ${notifTheme.optionHoverBorder}`}
                    >
                      <div className="min-w-0 pe-2 text-start">
                        <span className="block text-sm font-semibold text-slate-800 dark:text-slate-100">{t('emailUpdates')}</span>
                        <span className="mt-1 block text-xs leading-snug text-slate-500 dark:text-slate-400">
                          {t('emailUpdatesHint')}
                        </span>
                      </div>
                      <input
                        type="checkbox"
                        checked={prefs.emailNotifications}
                        onChange={(e) => updatePref('emailNotifications', e.target.checked)}
                        className={`mt-0.5 h-4 w-4 shrink-0 rounded border-slate-300 ${notifTheme.checkbox} ${notifTheme.checkboxFocus}`}
                      />
                    </label>
                    <InAppAlertsField
                      checked={prefs.pushNotifications || pushInstallOfferOpen}
                      onCheckedChange={handlePushNotificationsChange}
                      showInstallOffer={pushInstallOfferOpen}
                      focused={inAppAlertsFocused}
                      checkboxClassName={`${notifTheme.checkbox} ${notifTheme.checkboxFocus}`}
                      optionHoverClassName={notifTheme.optionHoverBorder}
                      onOfferOccupyingChange={handleInstallOfferOccupyingChange}
                    />
                  </div>

                  <div className="flex justify-center">
                  <button
                    type="button"
                    onClick={() => void handleSaveGoalsPrefs()}
                    disabled={!notifDirty || savingPrefs}
                    className={`flex w-full items-center justify-center gap-2 sm:w-auto ${notifTheme.saveButton}`}
                  >
                    {savingPrefs && <Loader2 className="h-4 w-4 animate-spin" />}
                    {t('savePreferences')}
                  </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </section>
              )}

              {tabId === 'lessonText' && (
        <section className={`mb-2 ${lessonTheme.section} ${sectionPadLast}`}>
          <div className={modalSectionHeaderRowClass}>
            <div className={lessonTheme.icon}>
              <BookOpen className="h-6 w-6" aria-hidden />
            </div>
            <div className="min-w-0 flex-1 text-start">
              <div className="mb-1 flex flex-wrap items-center gap-2">
                <h2 className="text-lg font-semibold tracking-tight text-slate-900 dark:text-white">{t('lessonReadingTitle')}</h2>
                <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${lessonTheme.badge}`}>
                  <SlidersHorizontal className="h-3 w-3" aria-hidden />
                  {t('textBlocksBadge')}
                </span>
              </div>
              <p className="mme-hint mb-5 max-w-prose text-xs text-slate-500 dark:text-slate-400">
                {t('lessonTextHint')}
              </p>
              <div data-tour-id="quick-settings-lesson-text-fonts" className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {LEARNER_PERSIAN_FONT_OPTIONS.map((opt) => {
                  const selected = persianFont === opt.id;
                  return (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => handlePersianFontSelect(opt.id)}
                      className={`rounded-xl border-2 p-4 text-start transition-all ${
                        selected ? lessonTheme.fontCardSelected : 'border-slate-200 dark:border-gray-600 hover:border-slate-300 dark:hover:border-gray-500 bg-white/80 dark:bg-gray-900/50'
                      }`}
                    >
                      <div className="mb-2 flex items-baseline justify-between gap-2">
                        <span className="font-medium text-slate-800 dark:text-slate-100">{opt.labelEn}</span>
                        <span className="text-sm text-slate-500 dark:text-slate-400">{opt.labelFa}</span>
                      </div>
                      <div
                        dir="rtl"
                        className="min-h-[3.5rem] text-end text-base leading-relaxed text-slate-900 dark:text-slate-100"
                        style={{ fontFamily: getLearnerPersianFontStack(opt.id) }}
                      >
                        {SAMPLE_FA}
                      </div>
                    </button>
                  );
                })}
              </div>

              <div data-tour-id="quick-settings-lesson-text-size">
              <div className={`mt-6 text-sm font-semibold text-slate-800 dark:text-slate-100 ${modalIconTitleRowClass}`}>
                <Sparkles className={`h-4 w-4 shrink-0 ${lessonTheme.iconAccent}`} aria-hidden />
                {t('readingSize')}
              </div>
              <p className="mme-hint mb-3 mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                {t('readingSizeHint')}
              </p>
              <div className="flex flex-wrap items-center gap-4">
                <input
                  type="range"
                  dir={direction}
                  min={40}
                  max={220}
                  step={1}
                  value={percent}
                  onChange={(e) => {
                    const v = Number(e.target.value) / 100;
                    setFontSize(v);
                  }}
                  className={`h-2 min-w-[200px] flex-1 ${lessonTheme.rangeAccent}`}
                  aria-valuemin={40}
                  aria-valuemax={220}
                  aria-valuenow={percent}
                  aria-label={t('lessonTextSizeAria')}
                />
                <span className="w-14 text-end font-mono text-sm text-slate-700 dark:text-slate-200">{percent}%</span>
                <button
                  type="button"
                  onClick={() => setFontSize(1)}
                  disabled={Math.abs(currentMultiplier - 1) < 0.001}
                  className="text-sm px-3 py-1.5 rounded-lg border border-slate-300 dark:border-gray-600 disabled:opacity-40 hover:bg-white dark:hover:bg-gray-700"
                >
                  {t('adjustTo100')}
                </button>
              </div>
              </div>

              <div
                data-tour-id="quick-settings-lesson-text-preview"
                dir="rtl"
                className="mt-4 min-h-[4rem] rounded-xl border border-slate-200/80 bg-white/90 p-3 text-end shadow-inner dark:border-gray-600 dark:bg-gray-900/70"
              >
                <div className="mb-2 text-xs font-medium text-slate-500 dark:text-slate-400">
                  {t('previewLabel')}
                </div>
                <FormattedTextBlock
                  textContent={SAMPLE_FA}
                  formatting={{ baseFontSize: '18px' }}
                  lessonTypography={{ stack: getLearnerPersianFontStack(persianFont), multiplier: currentMultiplier }}
                />
              </div>
              <div className="mt-3 flex flex-col items-center gap-3 sm:flex-row sm:flex-wrap sm:justify-center">
              <button
                type="button"
                onClick={() => void handleResetToDefaultFontAndSize()}
                disabled={loadingPrefs || resettingDefaults || savingLessonText}
                className="flex w-full items-center justify-center gap-2 rounded-xl border border-slate-300 px-4 py-2 text-sm font-medium text-slate-800 hover:bg-white disabled:opacity-50 dark:border-gray-600 dark:text-slate-100 dark:hover:bg-gray-700 sm:w-auto"
              >
                {resettingDefaults && <Loader2 className="h-4 w-4 animate-spin" />}
                {t('resetDefaultFontSize')}
              </button>
              <button
                type="button"
                onClick={() => void handleSaveLessonText()}
                disabled={!prefs || !lessonTextDirty || savingLessonText || loadingPrefs}
                className={`flex w-full items-center justify-center gap-2 sm:w-auto ${lessonTheme.saveButton}`}
              >
                {savingLessonText && <Loader2 className="h-4 w-4 animate-spin" />}
                {t('saveLessonText')}
              </button>
              </div>
            </div>
          </div>
        </section>
              )}
              {tabId === 'template' && (
                <section className="rounded-2xl border border-[#e4d5c3] bg-[#fffdf8] p-4 text-start shadow-sm dark:border-[#3d342c] dark:bg-[#1c1714] sm:p-5">
                  <h2 className="text-lg font-semibold tracking-tight text-slate-900 dark:text-white">
                    {t('tabPreferences', { defaultValue: isRtl ? 'ترجیحات' : 'Preferences' })}
                  </h2>
                  <p className="mt-1 text-sm leading-relaxed text-slate-600 dark:text-slate-300" style={{ textAlign: 'justify' }}>
                    {t('preferencesIntro', {
                      defaultValue: isRtl
                        ? 'قالب برنامه و حالت روشن یا تاریک را انتخاب کنید.'
                        : 'Choose the app theme and whether the app uses light or dark mode.',
                    })}
                  </p>

                  <h3 className="mt-5 text-sm font-semibold text-slate-900 dark:text-white">
                    {t('appThemeTitle', { defaultValue: isRtl ? 'قالب برنامه' : 'App theme' })}
                  </h3>
                  <p className="mt-1 text-sm leading-relaxed text-slate-600 dark:text-slate-300" style={{ textAlign: 'justify' }}>
                    {t('templateIntro', {
                      defaultValue: isRtl
                        ? 'خانه و بقیه صفحه‌های یادگیری با این قالب دیده می‌شوند.'
                        : 'Choose how the home and the other learner pages look.',
                    })}
                  </p>
                  <div className="mt-3 space-y-3" data-tour-id="quick-settings-template-choices">
                    {([
                      {
                        id: 'classic' as const,
                        Icon: LayoutList,
                        titleKey: 'homeDesignClassicShort',
                        titleFallback: 'Advanced - more details',
                        bodyKey: 'templateClassicBody',
                        bodyFallback:
                          'The full home. Every section stays open on one long page, with more detail on the cards, the course list, and the side menu.',
                      },
                      {
                        id: 'mme' as const,
                        Icon: Smile,
                        titleKey: 'homeDesignMmeShort',
                        titleFallback: 'Simple - easy to use',
                        bodyKey: 'templateSimpleBody',
                        bodyFallback:
                          'A calm home with large tiles. One section opens at a time, with shorter text. Leitner, Telegram, and chat sit in the side menu.',
                      },
                    ]).map((option) => {
                      const selected = homeDesign === option.id;
                      const Icon = option.Icon;
                      return (
                        <button
                          key={option.id}
                          type="button"
                          aria-pressed={selected}
                          onClick={() => setHomeDesign(option.id)}
                          className={`flex w-full items-start gap-3 rounded-xl border px-4 py-3 text-start transition-colors ${
                            selected
                              ? 'border-[#3f2a56] bg-[#3f2a56] text-[#fff8ee]'
                              : 'border-[#e4d5c3] bg-white text-[#2b221b] hover:bg-[#f7f1e8] dark:border-[#3d342c] dark:bg-[#261f1b] dark:text-[#f6efe6] dark:hover:bg-[#2a241f]'
                          }`}
                        >
                          <Icon className="mt-0.5 h-5 w-5 shrink-0" aria-hidden />
                          <span className="min-w-0 flex-1">
                            <span className="block text-sm font-semibold">
                              {t(option.titleKey, { defaultValue: option.titleFallback })}
                            </span>
                            <span
                              className={`mt-1 block text-xs leading-relaxed sm:text-sm ${
                                selected ? 'text-[#fff8ee]/90' : 'text-slate-600 dark:text-slate-300'
                              }`}
                              style={{ textAlign: 'justify' }}
                            >
                              {t(option.bodyKey, { defaultValue: option.bodyFallback })}
                            </span>
                          </span>
                        </button>
                      );
                    })}
                  </div>

                  <h3 className="mt-6 text-sm font-semibold text-slate-900 dark:text-white">
                    {t('colorModeTitle', { defaultValue: isRtl ? 'حالت روشن و تاریک' : 'Light and dark mode' })}
                  </h3>
                  <p className="mt-1 text-sm leading-relaxed text-slate-600 dark:text-slate-300" style={{ textAlign: 'justify' }}>
                    {t('colorModeHint', {
                      defaultValue: isRtl
                        ? 'ظاهر روشن یا تاریک برنامه را انتخاب کنید.'
                        : 'Choose a light or dark appearance for the app.',
                    })}
                  </p>
                  <div className="mt-3 space-y-3" data-tour-id="quick-settings-template-mode">
                    {([
                      {
                        id: 'light' as const,
                        Icon: Sun,
                        title: isRtl ? 'حالت روشن' : 'Light mode',
                        body: isRtl ? 'پس‌زمینه روشن برای استفاده در روز.' : 'A light background for daytime use.',
                      },
                      {
                        id: 'dark' as const,
                        Icon: Moon,
                        title: isRtl ? 'حالت تاریک' : 'Dark mode',
                        body: isRtl ? 'پس‌زمینه تیره برای استفاده در شب.' : 'A dark background for nighttime use.',
                      },
                    ]).map((option) => {
                      const selected = theme === option.id;
                      const Icon = option.Icon;
                      return (
                        <button
                          key={option.id}
                          type="button"
                          aria-pressed={selected}
                          disabled={colorModePending}
                          onClick={() => {
                            if (colorModePending || theme === option.id) return;
                            setColorModePending(true);
                            void toggleTheme().finally(() => setColorModePending(false));
                          }}
                          className={`flex w-full items-start gap-3 rounded-xl border px-4 py-3 text-start transition-colors disabled:opacity-70 ${
                            selected
                              ? 'border-[#3f2a56] bg-[#3f2a56] text-[#fff8ee]'
                              : 'border-[#e4d5c3] bg-white text-[#2b221b] hover:bg-[#f7f1e8] dark:border-[#3d342c] dark:bg-[#261f1b] dark:text-[#f6efe6] dark:hover:bg-[#2a241f]'
                          }`}
                        >
                          <Icon className="mt-0.5 h-5 w-5 shrink-0" aria-hidden />
                          <span className="min-w-0 flex-1">
                            <span className="block text-sm font-semibold">{option.title}</span>
                            <span
                              className={`mt-1 block text-xs leading-relaxed sm:text-sm ${
                                selected ? 'text-[#fff8ee]/90' : 'text-slate-600 dark:text-slate-300'
                              }`}
                              style={{ textAlign: 'justify' }}
                            >
                              {option.body}
                            </span>
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </section>
              )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <>
            <SectionPillSeparator label={t('sectionRegion')} className="mb-8 mt-0" />

            <section
              className={`rounded-2xl border border-slate-300/90 bg-gradient-to-br from-white via-slate-50/90 to-teal-50/50 shadow-md shadow-slate-300/50 dark:border-gray-600/90 dark:from-gray-800 dark:via-gray-800/95 dark:to-teal-950/30 dark:shadow-black/30 overflow-hidden ${sectionPad}`}
            >
              <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:gap-5">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-teal-500 to-cyan-600 text-white shadow-lg shadow-teal-500/30">
                  <Globe className="h-6 w-6" aria-hidden />
                </div>
                <div className="min-w-0 flex-1">
                  <h2 className="mb-1 text-lg font-semibold tracking-tight text-slate-900 dark:text-white">{t('regionTitle')}</h2>
                  <p className="mme-hint mb-5 max-w-prose text-xs text-slate-500 dark:text-slate-400">
                    {t('regionHint')}
                  </p>
                  {loadingPrefs || !prefs ? (
                    <div className="flex items-center justify-center gap-2 rounded-xl border border-dashed border-slate-200 bg-white/50 py-6 text-slate-600 dark:border-gray-600 dark:bg-gray-900/40 dark:text-slate-300">
                      <Loader2 className="h-6 w-6 animate-spin text-teal-600 dark:text-teal-400" />
                      <span>{t('loadingPreferences')}</span>
                    </div>
                  ) : (
                    <>
                      <div className="mb-6 rounded-xl border border-slate-200/80 bg-white/60 p-4 dark:border-gray-600 dark:bg-gray-900/40">
                        <p className="mb-1 text-sm font-medium text-slate-800 dark:text-slate-100">{t('preferredLanguage')}</p>
                        <p className="mme-hint mb-3 text-xs text-slate-500 dark:text-slate-400">{t('preferredLanguageHint')}</p>
                        <LanguageSwitcher variant="settings" />
                      </div>

                      <label htmlFor="settings-tz-page" className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-200">
                        {t('timezone')}
                      </label>
                      <div id="settings-tz-page">
                        <ProfileComboList
                          value={resolveTimeZone(prefs.timeZone)}
                          onChange={(tz) => updatePref('timeZone', resolveTimeZone(tz))}
                          options={tzOptions}
                          searchable
                          searchPlaceholder={t('searchTimeZones')}
                          placeholder={t('selectTimeZone')}
                          id="learner-settings-timezone-page"
                          aria-label={t('timezone')}
                          useFixedPortal
                          closeOnOutsideClick={false}
                          accentSelectedClass="bg-teal-50 dark:bg-teal-950/40 text-teal-900 dark:text-teal-100"
                          accentCheckClass="text-teal-600 dark:text-teal-400"
                          triggerClassName="border-slate-200 dark:border-gray-600"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => void handleSaveRegion()}
                        disabled={!regionDirty || savingRegion || loadingPrefs}
                        className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-teal-600 px-6 py-2.5 text-sm font-semibold text-white shadow-md shadow-teal-600/20 transition-colors hover:bg-teal-700 disabled:opacity-50 sm:w-auto"
                      >
                        {savingRegion && <Loader2 className="h-4 w-4 animate-spin" />}
                        {t('saveRegion')}
                      </button>
                    </>
                  )}
                </div>
              </div>
            </section>

            <SectionPillSeparator label={t('sectionSecurity')} className="my-10" />
            <LearnerTwoFactorSection theme={securityTheme} sectionPad={sectionPad} isModal={false} />

            <SectionPillSeparator label={t('sectionNotifications')} className="my-10" />

            <section
              className={`rounded-2xl border border-slate-300/90 bg-gradient-to-br from-white via-amber-50/30 to-slate-50/90 shadow-md shadow-slate-300/50 dark:border-gray-600/90 dark:from-gray-800 dark:via-amber-950/25 dark:to-gray-900 dark:shadow-black/30 overflow-hidden ${sectionPad}`}
            >
              <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:gap-5">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 text-white shadow-lg shadow-amber-500/25">
                  <Bell className="h-6 w-6" aria-hidden />
                </div>
                <div className="min-w-0 flex-1">
                  <h2 className="mb-1 text-lg font-semibold tracking-tight text-slate-900 dark:text-white">{t('notifications')}</h2>
                  <p className="mme-hint mb-4 max-w-prose text-xs text-slate-500 dark:text-slate-400">
                    {t('notificationsHint')}
                  </p>
                  {loadingPrefs || !prefs ? (
                    <div className="flex items-center justify-center gap-2 rounded-xl border border-dashed border-slate-200 bg-white/50 py-6 text-slate-600 dark:border-gray-600 dark:bg-gray-900/40 dark:text-slate-300">
                      <Loader2 className="h-6 w-6 animate-spin text-teal-600 dark:text-teal-400" />
                      <span>{t('loadingPreferences')}</span>
                    </div>
                  ) : (
                    <>
                      <p className="mb-1 text-xs font-medium text-slate-600 dark:text-slate-300">{t('howWeReachYou')}</p>
                      <p className="mme-hint mb-3 max-w-prose text-[11px] leading-relaxed text-slate-500 dark:text-slate-400">
                        {t('howWeReachYouHint')}
                      </p>
                      <div className={`mb-6 grid grid-cols-1 gap-3 ${installOfferOccupying ? '' : 'sm:grid-cols-2'}`}>
                        <label className="flex cursor-pointer items-start justify-between gap-3 rounded-xl border border-slate-200/90 bg-white/95 px-4 py-3.5 shadow-sm transition-colors hover:border-teal-300 dark:border-gray-600 dark:bg-gray-900/60 dark:hover:border-teal-800">
                          <div className="min-w-0 pr-2">
                            <span className="block text-sm font-semibold text-slate-800 dark:text-slate-100">{t('emailUpdates')}</span>
                            <span className="mt-1 block text-xs leading-snug text-slate-500 dark:text-slate-400">
                              {t('emailUpdatesHint')}
                            </span>
                          </div>
                          <input
                            type="checkbox"
                            checked={prefs.emailNotifications}
                            onChange={(e) => updatePref('emailNotifications', e.target.checked)}
                            className="mt-0.5 h-4 w-4 shrink-0 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                          />
                        </label>
                        <InAppAlertsField
                          checked={prefs.pushNotifications || pushInstallOfferOpen}
                          onCheckedChange={handlePushNotificationsChange}
                          showInstallOffer={pushInstallOfferOpen}
                          focused={inAppAlertsFocused}
                          checkboxClassName="text-teal-600 focus:ring-teal-500"
                          optionHoverClassName="hover:border-teal-300 dark:hover:border-teal-800"
                          onOfferOccupyingChange={handleInstallOfferOccupyingChange}
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => void handleSaveGoalsPrefs()}
                        disabled={!notifDirty || savingPrefs}
                        className="flex w-full items-center justify-center gap-2 rounded-xl bg-teal-600 px-6 py-2.5 text-sm font-semibold text-white shadow-md shadow-teal-600/20 transition-colors hover:bg-teal-700 disabled:opacity-50 sm:w-auto"
                      >
                        {savingPrefs && <Loader2 className="h-4 w-4 animate-spin" />}
                        {t('savePreferences')}
                      </button>
                    </>
                  )}
                </div>
              </div>
            </section>

            <SectionPillSeparator label={t('sectionLessonText')} className="my-10" />

            <section
              className={`mb-2 rounded-2xl border border-slate-300/90 bg-gradient-to-br from-white via-violet-50/40 to-slate-50/90 shadow-md shadow-slate-300/40 dark:border-gray-600/90 dark:from-gray-800 dark:via-violet-950/20 dark:to-gray-900 dark:shadow-black/30 overflow-hidden ${sectionPadLast}`}
            >
              <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:gap-5">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-violet-500 to-indigo-600 text-white shadow-lg shadow-violet-500/30">
                  <BookOpen className="h-6 w-6" aria-hidden />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="mb-1 flex flex-wrap items-center gap-2">
                    <h2 className="text-lg font-semibold tracking-tight text-slate-900 dark:text-white">{t('lessonReadingTitle')}</h2>
                    <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-500 dark:bg-gray-700/80 dark:text-slate-400">
                      <SlidersHorizontal className="h-3 w-3" aria-hidden />
                      {t('textBlocksBadge')}
                    </span>
                  </div>
                  <p className="mme-hint mb-5 max-w-prose text-xs text-slate-500 dark:text-slate-400">
                    {t('lessonTextHint')}
                  </p>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    {LEARNER_PERSIAN_FONT_OPTIONS.map((opt) => {
                      const selected = persianFont === opt.id;
                      return (
                        <button
                          key={opt.id}
                          type="button"
                          onClick={() => handlePersianFontSelect(opt.id)}
                          className={`rounded-xl border-2 p-4 text-left transition-all ${
                            selected
                              ? 'border-teal-500 bg-teal-50/90 shadow-sm ring-1 ring-teal-500/20 dark:border-teal-400 dark:bg-teal-950/50'
                              : 'border-slate-200 bg-white/80 hover:border-slate-300 dark:border-gray-600 dark:bg-gray-900/50 dark:hover:border-gray-500'
                          }`}
                        >
                          <div className="mb-2 flex items-baseline justify-between gap-2">
                            <span className="font-medium text-slate-800 dark:text-slate-100">{opt.labelEn}</span>
                            <span className="text-sm text-slate-500 dark:text-slate-400">{opt.labelFa}</span>
                          </div>
                          <div
                            dir="rtl"
                            className="min-h-[3.5rem] text-right text-base leading-relaxed text-slate-900 dark:text-slate-100"
                            style={{ fontFamily: getLearnerPersianFontStack(opt.id) }}
                          >
                            {SAMPLE_FA}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                  <div className="mt-6 flex items-center gap-2 text-sm font-semibold text-slate-800 dark:text-slate-100">
                    <Sparkles className="h-4 w-4 shrink-0 text-violet-500" aria-hidden />
                    {t('readingSize')}
                  </div>
                  <p className="mme-hint mb-3 mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                    {t('readingSizeHint')}
                  </p>
                  <div className="flex flex-wrap items-center gap-4">
                    <input
                      type="range"
                      min={40}
                      max={220}
                      step={1}
                      value={percent}
                      onChange={(e) => setFontSize(Number(e.target.value) / 100)}
                      className="h-2 min-w-[200px] flex-1 accent-teal-600"
                      aria-valuemin={40}
                      aria-valuemax={220}
                      aria-valuenow={percent}
                      aria-label={t('lessonTextSizeAria')}
                    />
                    <span className="w-14 text-right font-mono text-sm text-slate-700 dark:text-slate-200">{percent}%</span>
                    <button
                      type="button"
                      onClick={() => setFontSize(1)}
                      disabled={Math.abs(currentMultiplier - 1) < 0.001}
                      className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm hover:bg-white disabled:opacity-40 dark:border-gray-600 dark:hover:bg-gray-700"
                    >
                      {t('adjustTo100')}
                    </button>
                  </div>
                  <div
                    dir="rtl"
                    className="mt-4 min-h-[4rem] rounded-xl border border-slate-200/80 bg-white/90 p-3 text-right shadow-inner dark:border-gray-600 dark:bg-gray-900/70"
                  >
                    <div className="mb-2 text-right text-xs font-medium text-slate-500 dark:text-slate-400">
                      {t('previewLabel')}
                    </div>
                    <FormattedTextBlock
                      textContent={SAMPLE_FA}
                      formatting={{ baseFontSize: '18px' }}
                      lessonTypography={{ stack: getLearnerPersianFontStack(persianFont), multiplier: currentMultiplier }}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => void handleResetToDefaultFontAndSize()}
                    disabled={loadingPrefs || resettingDefaults || savingLessonText}
                    className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl border border-slate-300 px-4 py-2 text-sm font-medium text-slate-800 hover:bg-white disabled:opacity-50 dark:border-gray-600 dark:text-slate-100 dark:hover:bg-gray-700 sm:w-auto"
                  >
                    {resettingDefaults && <Loader2 className="h-4 w-4 animate-spin" />}
                    {t('resetDefaultFontSize')}
                  </button>
                  <button
                    type="button"
                    onClick={() => void handleSaveLessonText()}
                    disabled={!prefs || !lessonTextDirty || savingLessonText || loadingPrefs}
                    className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-teal-600 to-cyan-600 px-6 py-2.5 text-sm font-semibold text-white shadow-md hover:from-teal-700 hover:to-cyan-700 disabled:opacity-50 sm:w-auto"
                  >
                    {savingLessonText && <Loader2 className="h-4 w-4 animate-spin" />}
                    {t('saveLessonText')}
                  </button>
                </div>
              </div>
            </section>
          </>
        )}
      </div>

      {isModal && (
        <footer className={`flex w-full shrink-0 items-center justify-center gap-2.5 border-t px-4 py-3 sm:px-5 ${LEARNER_SETTINGS_MODAL_CHROME_CLASS}`}>
          <button
            type="button"
            onClick={() => dismiss?.()}
            className="rounded-xl border border-slate-400/80 bg-white/95 px-4 py-2.5 text-sm font-semibold text-slate-800 shadow-sm transition-colors hover:bg-slate-50 dark:border-slate-500 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700"
          >
            {tCommon('close')}
          </button>
          <button
            type="button"
            onClick={() => void handleSaveAll()}
            disabled={!hasUnsavedChanges || savingAll || loadingPrefs}
            className="flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-violet-600 to-indigo-600 px-5 py-2.5 text-sm font-semibold text-white shadow-md shadow-violet-900/20 transition-colors hover:from-violet-700 hover:to-indigo-700 disabled:opacity-50 dark:shadow-violet-950/40"
          >
            {savingAll && <Loader2 className="h-4 w-4 animate-spin" />}
            {t('saveAllAndClose')}
          </button>
        </footer>
      )}
    </div>
  );
});
